import React from 'react';
import styled from 'styled-components';
import { IBatchJob, IBatchProgress, BatchJobStep, BatchJobStatus, BatchJobMode } from 'models/TranslationBatchModel';

interface IProps {
  job: IBatchJob;
  progress?: IBatchProgress | null;
}

interface StepDef {
  step: number;
  label: string;
}

type StepStatus = 'done' | 'active' | 'pending' | 'failed';

interface StepRender {
  // Detail line shown right of the label (e.g. "8000/13839 processed · 5397 hits")
  detail?: React.ReactNode;
  // Sub-phase label shown when the step is active (e.g. "Building", "Waiting AI", "Applying")
  phaseLabel?: string;
  // 0..1 — drives the inline progress bar fill
  ratio?: number;
  // Inline failure count
  failedCount?: number;
}

const formatElapsed = (startedAt: string | null): string | null => {
  if (!startedAt) return null;
  // Backend formats as "Y-m-d H:i:s" UTC. Treat as UTC for parsing safety across browsers.
  const ts = Date.parse(startedAt.replace(' ', 'T') + 'Z');
  if (Number.isNaN(ts)) return null;
  const elapsedMs = Date.now() - ts;
  if (elapsedMs < 0) return null;
  const seconds = Math.floor(elapsedMs / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ${minutes % 60}m`;
};

const pct = (ratio: number | undefined): string => {
  if (ratio === undefined || !Number.isFinite(ratio)) return '';
  return `${Math.max(0, Math.min(100, Math.round(ratio * 100)))}%`;
};

const UnifiedBatchSummary: React.FC<IProps> = ({ job, progress }) => {
  const isMemoryOnly = job.mode === BatchJobMode.MEMORY_ONLY;

  const steps: StepDef[] = [
    { step: BatchJobStep.MEMORY, label: 'Memory' },
    ...(isMemoryOnly
      ? []
      : [
          { step: BatchJobStep.RB_PB, label: 'RB + PB' },
          { step: BatchJobStep.TRANSLATE, label: 'Translate' },
          { step: BatchJobStep.VALIDATE, label: 'Validate' },
          { step: BatchJobStep.RETRY, label: 'Retry' },
          { step: BatchJobStep.MEMORY_SIBLINGS, label: 'Awaiting Memory' },
        ]),
  ];

  const getStepStatus = (stepDef: StepDef): StepStatus => {
    if (job.status === BatchJobStatus.FAILED) {
      if (job.currentStep === stepDef.step) return 'failed';
      if (job.currentStep > stepDef.step) return 'done';
      return 'pending';
    }
    if (job.status === BatchJobStatus.CANCELLED) {
      if (job.currentStep > stepDef.step) return 'done';
      return 'pending';
    }
    if (job.currentStep > stepDef.step) return 'done';
    if (job.currentStep === stepDef.step) return 'active';
    return 'pending';
  };

  const renderForStep = (step: number, status: StepStatus): StepRender => {
    if (step === BatchJobStep.MEMORY) {
      const processed = job.memoryProcessed;
      const hits = job.memoryTranslated;
      const total = job.totalItems;
      // While running we may not know the final processed total — pessimistic ratio uses totalItems.
      const denom = total > 0 ? total : Math.max(processed, 1);
      return {
        detail: (
          <>
            <strong>{processed.toLocaleString()}</strong>/{total.toLocaleString()} processed · {hits.toLocaleString()} hits
          </>
        ),
        ratio: processed / denom,
      };
    }

    if (step === BatchJobStep.MEMORY_SIBLINGS) {
      const processed = job.memorySiblingsProcessed;
      const hits = job.memorySiblingsTranslated;
      return {
        detail: <><strong>{processed.toLocaleString()}</strong> processed · {hits.toLocaleString()} hits</>,
        ratio: processed > 0 ? hits / processed : undefined,
      };
    }

    if (step === BatchJobStep.RB_PB) {
      if (status !== 'active') return {};
      const rb = progress?.slots.find((s) => s.slot === 0);
      const pb = progress?.slots.find((s) => s.slot === 1);
      if (!rb && !pb) return { phaseLabel: 'Submitting' };
      const ratio = ((rb?.completed ?? 0) + (pb?.completed ?? 0)) /
        Math.max(1, (rb?.total ?? 0) + (pb?.total ?? 0));
      return {
        phaseLabel: 'Waiting AI',
        detail: (
          <>
            {rb && (rb.error ? <Err title={rb.error}>R: err</Err> : <>R: {rb.completed}/{rb.total}</>)}
            {rb && pb && <Sep />}
            {pb && (pb.error ? <Err title={pb.error}>B: err</Err> : <>B: {pb.completed}/{pb.total}</>)}
          </>
        ),
        ratio,
      };
    }

    // TRANSLATE / VALIDATE / RETRY share the same three sub-phases.
    if (step === BatchJobStep.TRANSLATE || step === BatchJobStep.VALIDATE || step === BatchJobStep.RETRY) {
      // Done — show the persistent counter.
      if (status === 'done') {
        const finalCount = step === BatchJobStep.TRANSLATE ? job.aiTranslated
          : step === BatchJobStep.VALIDATE ? job.aiValidated
          : job.aiRetried;
        const failed = step === BatchJobStep.TRANSLATE ? job.aiFailed : 0;
        return {
          detail: <>{finalCount.toLocaleString()}</>,
          ratio: 1,
          failedCount: failed > 0 ? failed : undefined,
        };
      }
      if (status !== 'active') return {};

      const slot = progress?.slots.find((s) => s.slot === 0);
      const stepIngested = job.stepIngested;
      const stepTotal = job.stepTotal;

      // Build phase: providerBatchId === null while providerBatchStep is the relevant step.
      const expectedBatchStep = step === BatchJobStep.TRANSLATE ? 'translate'
        : step === BatchJobStep.VALIDATE ? 'validate'
        : 'retry';
      const isBuilding = job.providerBatchId === null && job.providerBatchStep === expectedBatchStep;

      if (isBuilding) {
        return {
          phaseLabel: 'Building',
          detail: stepTotal > 0
            ? <><strong>{stepIngested.toLocaleString()}</strong>/{stepTotal.toLocaleString()} requests</>
            : <>Preparing…</>,
          ratio: stepTotal > 0 ? stepIngested / stepTotal : undefined,
        };
      }

      // Submitted — provider slot tells us whether AI is still working or done.
      if (slot) {
        const aiInProgress = slot.status === 'in_progress' || slot.status === 'pending';
        if (aiInProgress) {
          return {
            phaseLabel: 'Waiting AI',
            detail: slot.error
              ? <Err title={slot.error}>err</Err>
              : <><strong>{slot.completed.toLocaleString()}</strong>/{slot.total.toLocaleString()}</>,
            ratio: slot.total > 0 ? slot.completed / slot.total : undefined,
            failedCount: slot.failed > 0 ? slot.failed : undefined,
          };
        }
        // AI finished — we're applying results to TPTs.
        return {
          phaseLabel: 'Applying',
          detail: stepTotal > 0
            ? <><strong>{stepIngested.toLocaleString()}</strong>/{stepTotal.toLocaleString()} applied</>
            : <>{stepIngested.toLocaleString()} applied</>,
          ratio: stepTotal > 0 ? stepIngested / stepTotal : undefined,
          failedCount: slot.failed > 0 ? slot.failed : undefined,
        };
      }

      // Provider hasn't responded with slot data yet — fall back to whatever counter exists.
      const fallback = step === BatchJobStep.TRANSLATE ? job.aiTranslated
        : step === BatchJobStep.VALIDATE ? job.aiValidated
        : job.aiRetried;
      return {
        phaseLabel: 'Waiting AI',
        detail: <>{fallback.toLocaleString()}</>,
      };
    }

    return {};
  };

  return (
    <Container>
      {steps.map((stepDef) => {
        const status = getStepStatus(stepDef);
        const render = renderForStep(stepDef.step, status);
        const elapsed = status === 'active' ? formatElapsed(job.stepStartedAt) : null;
        return (
          <StepRow key={stepDef.step} $status={status}>
            <ProgressFill $ratio={render.ratio ?? 0} $status={status} />
            <RowContent>
              <StepIcon $status={status}>{getIcon(status)}</StepIcon>
              <StepLabel $active={status === 'active'}>{stepDef.label}</StepLabel>
              {render.phaseLabel && <PhaseLabel>{render.phaseLabel}</PhaseLabel>}
              {render.detail && <Detail $active={status === 'active'}>{render.detail}</Detail>}
              {render.ratio !== undefined && status !== 'pending' && <Pct>{pct(render.ratio)}</Pct>}
              {render.failedCount !== undefined && <FailedInline>· {render.failedCount} failed</FailedInline>}
              {elapsed && <Elapsed>· {elapsed}</Elapsed>}
            </RowContent>
          </StepRow>
        );
      })}
      {job.errorMessage && job.status === BatchJobStatus.FAILED && <ErrorMsg>{job.errorMessage}</ErrorMsg>}
    </Container>
  );
};

const getIcon = (status: StepStatus) => {
  switch (status) {
    case 'done': return '✓';
    case 'active': return '●';
    case 'failed': return '✗';
    default: return '○';
  }
};

export default UnifiedBatchSummary;

const Container = styled.div`
  padding: 12px 16px;
  background: #fafafa;
  border-bottom: 1px solid #e0e0e0;
`;

const StepRow = styled.div<{ $status: StepStatus }>`
  position: relative;
  padding: 6px 8px;
  margin: 2px -4px;
  border-radius: 4px;
  font-size: 13px;
  opacity: ${(p) => (p.$status === 'pending' ? 0.45 : 1)};
  background: ${(p) => (p.$status === 'active' ? '#eaf3fc' : 'transparent')};
  ${(p) => p.$status === 'active' && 'box-shadow: inset 3px 0 0 #1976d2;'}
  overflow: hidden;
`;

const ProgressFill = styled.div<{ $ratio: number; $status: StepStatus }>`
  position: absolute;
  inset: 0;
  width: ${(p) => Math.max(0, Math.min(100, p.$ratio * 100))}%;
  background: ${(p) => {
    if (p.$status === 'done') return 'rgba(76, 175, 80, 0.10)';
    if (p.$status === 'active') return 'rgba(33, 150, 243, 0.18)';
    if (p.$status === 'failed') return 'rgba(244, 67, 54, 0.12)';
    return 'transparent';
  }};
  transition: width 0.3s ease;
  pointer-events: none;
`;

const RowContent = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const StepIcon = styled.span<{ $status: StepStatus }>`
  width: 16px;
  text-align: center;
  font-size: 12px;
  color: ${(p) => {
    switch (p.$status) {
      case 'done': return '#4caf50';
      case 'active': return '#1976d2';
      case 'failed': return '#f44336';
      default: return '#999';
    }
  }};
`;

const StepLabel = styled.span<{ $active: boolean }>`
  font-weight: ${(p) => (p.$active ? 700 : 500)};
`;

const PhaseLabel = styled.span`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #1565c0;
  background: #e3f2fd;
  padding: 1px 6px;
  border-radius: 8px;
`;

const Detail = styled.span<{ $active: boolean }>`
  color: ${(p) => (p.$active ? '#1565c0' : '#666')};
  font-size: 12px;
  font-variant-numeric: tabular-nums;
`;

const Pct = styled.span`
  color: #666;
  font-size: 11px;
  font-variant-numeric: tabular-nums;
`;

const FailedInline = styled.span`
  color: #c62828;
  font-size: 12px;
`;

const Elapsed = styled.span`
  color: #888;
  font-size: 11px;
  margin-left: auto;
`;

const Sep = styled.span`
  display: inline-block;
  width: 12px;
`;

const Err = styled.span`
  color: #c62828;
  font-size: 11px;
  font-style: italic;
`;

const ErrorMsg = styled.div`
  margin-top: 6px;
  padding: 6px 8px;
  background: #ffebee;
  border-radius: 3px;
  color: #c62828;
  font-size: 12px;
  word-break: break-word;
`;
